<template>
  <div class="admin-container">
    <!-- Sidebar -->
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <!-- Consistent booking table for both AdminDashboard and AdminBooking -->
        <div class="data-section">
          <div class="section-header">
            <h2>{{ isDashboard ? 'Đặt Vé Gần Đây' : 'Quản Lý Đặt Vé' }}</h2>
            <div class="header-actions">
              <button
                v-if="canExport && !isDashboard"
                class="export-btn"
                @click="openExportModal"
                title="Xuất danh sách đặt vé ra Excel"
              >
                <i class="fas fa-file-excel"></i> Xuất danh sách
              </button>
              <div class="search-filters">
              <div class="search-box">
                <i class="fas fa-search"></i>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Tìm kiếm theo tên, tuyến đường..."
                  @keyup.enter="applySearch"
                />
                <button class="search-btn" @click="applySearch">
                  <i class="fas fa-search"></i>
                </button>
              </div>
              <div class="active-filters" v-if="groupFilter">
                <div class="filter-tag">
                  <span>Nhóm #{{ groupFilter }}</span>
                  <button @click="clearGroupFilter" class="clear-filter">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>

          <!-- Mobile-friendly filter accordion -->
          <div class="filter-accordion">
            <div class="filter-header" @click="toggleFilters">
              <span><i class="fas fa-filter"></i> Bộ Lọc</span>
              <i
                :class="
                  showFilters ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
                "
              ></i>
            </div>
            <div class="filter-body" :class="{ 'filter-open': showFilters }">
              <div class="quick-filters">
                <button
                  v-for="filter in dateFilters"
                  :key="filter.label"
                  class="quick-filter-btn"
                  :class="{ active: currentFilter === filter.label }"
                  @click="applyQuickFilter(filter.label)"
                >
                  {{ filter.label }}
                </button>
              </div>
              <div class="filter-inputs">
                <div class="filter-group">
                  <label><i class="fas fa-calendar-alt"></i> Từ:</label>
                  <input
                    type="date"
                    v-model="dateRange.from"
                    @change="handleDateChange"
                  />
                </div>
                <div class="filter-group">
                  <label><i class="fas fa-calendar-alt"></i> Đến:</label>
                  <input
                    type="date"
                    v-model="dateRange.to"
                    @change="handleDateChange"
                  />
                </div>
                <div class="filter-group">
                  <label><i class="fas fa-tag"></i> Trạng thái:</label>
                  <select v-model="statusFilter" class="filter-select">
                    <option value="">Tất cả trạng thái</option>
                    <option value="pending">Chờ xác nhận</option>
                    <option value="confirmed">Đã xác nhận</option>
                    <option value="paid">Đã thanh toán</option>
                    <option value="paying">Đang thanh toán</option>
                    <option value="canceled">Đã hủy</option>
                    <option value="refund">Hoàn tiền</option>
                    <option value="exchange">Đổi vé</option>
                    <option value="reserved">Bảo lưu</option>
                    <option value="refund_90">Hoàn 90%</option>
                    <option value="refund_80">Hoàn 80%</option>
                    <option value="refund_70">Hoàn 70%</option>
                    <option value="refund_50">Hoàn 50%</option>
                    <option value="no_refund">Không hoàn vé</option>
                    <option value="contacted">Đã liên hệ</option>
                  </select>
                </div>
                <button class="apply-filter-btn" @click="fetchData">
                  <i class="fas fa-check"></i> Áp dụng
                </button>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Mã</th>
                  <th>Khách Hàng</th>
                  <th>Tuyến Đường</th>
                  <th>Thời gian</th>
                  <th>Trạng Thái</th>
                  <th>Hóa đơn</th>
                  <th>Thao Tác</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="booking in filteredBookings"
                  :key="booking.id"
                  :class="{
                    'group-row': booking.groupId,
                    'highlight-row':
                      selectedBooking &&
                      booking.groupId &&
                      selectedBooking.groupId === booking.groupId
                  }"
                  :style="
                    booking.groupId
                      ? {
                          'border-left-color': getGroupColor(booking.groupId),
                          'border-left-width':
                            selectedBooking &&
                            selectedBooking.groupId === booking.groupId
                              ? '6px'
                              : '4px'
                        }
                      : {}
                  "
                >
                  <td class="booking-id">#{{ booking.id }}</td>
                  <td class="customer-name">
                    {{ booking.customerName }}
                    <span
                      v-if="booking.cccd"
                      class="leader-star-table"
                      title="Trưởng nhóm (có CCCD)"
                    >★</span>
                    <span
                      v-if="booking.groupId"
                      class="group-indicator"
                      :style="{
                        backgroundColor: getGroupColor(booking.groupId)
                      }"
                      @click.stop="showGroupBookings(booking.groupId)"
                      title="Xem tất cả vé trong nhóm này"
                    >
                      <i class="fas fa-users"></i>
                    </span>
                  </td>
                  <td class="destination">{{ booking.route }}</td>
                  <td class="booking-date">{{ formatDate(booking.date) }}</td>
                  <td class="status">
                    <span
                      class="status-badge"
                      :class="booking.status.toLowerCase()"
                    >
                      {{ translateStatus(booking.status) }}
                    </span>
                  </td>
                  <td class="invoice-status">
                    <router-link
                      v-if="booking.invoice"
                      :to="'/invoices/' + booking.invoice.invoice_request_id"
                      class="invoice-link"
                      :title="'Xem hóa đơn #' + booking.invoice.invoice_request_id"
                    >
                      <i class="fas fa-file-invoice"></i> {{ invoiceStatusLabel(booking.invoice.invoice_status) }}
                    </router-link>
                    <span v-else class="no-invoice">—</span>
                  </td>
                  <td class="actions">
                    <button
                      @click="openBookingModal(booking)"
                      class="action-btn view-btn"
                    >
                      <i class="fas fa-eye"></i
                      >
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredBookings.length === 0">
                  <td colspan="6" class="no-data">
                    <div class="empty-state">
                      <i class="fas fa-ticket-alt"></i>
                      <p>Không có dữ liệu đặt vé</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination">
            <button
              class="pagination-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <i class="fas fa-chevron-left"></i> Trước
            </button>
            <span class="page-info">
              Trang {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              class="pagination-btn"
              :disabled="
                currentPage === totalPages || filteredBookings.length === 0
              "
              @click="changePage(currentPage + 1)"
            >
              Sau <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Export Modal -->
    <div class="modal" v-if="showExportModal" @click.self="showExportModal = false">
      <div class="modal-content export-modal-content">
        <div class="modal-header">
          <h2><i class="fas fa-file-excel" style="color:#1d6f42"></i> Xuất danh sách đặt vé</h2>
          <button class="close-btn" @click="showExportModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="export-form">
            <div class="export-date-row">
              <div class="export-field">
                <label><i class="fas fa-calendar-alt"></i> Từ ngày</label>
                <input type="date" v-model="exportDateRange.from" class="export-date-input" />
              </div>
              <div class="export-field">
                <label><i class="fas fa-calendar-alt"></i> Đến ngày</label>
                <input type="date" v-model="exportDateRange.to" class="export-date-input" />
              </div>
            </div>

            <div class="export-info">
              <i class="fas fa-info-circle"></i>
              Sẽ xuất <strong>toàn bộ</strong> đặt vé trong khoảng thời gian chỉ định
              (từ 00:00 ngày bắt đầu đến hết ngày kết thúc),
              <strong>không bao gồm vé đã hủy</strong>, sắp xếp theo thứ tự đặt vé.
            </div>

            <div v-if="isExporting" class="export-loading">
              <i class="fas fa-spinner fa-spin"></i>
              {{ exportProgress }}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showExportModal = false" :disabled="isExporting">Đóng</button>
          <button
            class="export-confirm-btn"
            @click="exportBookings"
            :disabled="isExporting || !exportDateRange.from || !exportDateRange.to"
          >
            <i class="fas fa-download"></i>
            {{ isExporting ? 'Đang xuất...' : 'Xuất Excel' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Booking Modal - Consistent for both components -->
    <div class="modal" v-if="selectedBooking" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Chi Tiết Đặt Vé</h2>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <div
            class="booking-summary"
            :class="{ 'has-group': selectedBooking.groupId }"
            :style="
              selectedBooking.groupId
                ? {
                    'border-left-color': getGroupColor(selectedBooking.groupId)
                  }
                : {}
            "
          >
            <div class="booking-id-badge">
              Mã đặt vé #{{ selectedBooking.id }}
            </div>
            <div
              :class="['booking-status', selectedBooking.status.toLowerCase()]"
            >
              {{ translateStatus(selectedBooking.status) }}
            </div>
          </div>
          <div
            v-if="selectedBooking.groupId"
            class="group-info-section"
            :style="{ 'border-color': getGroupColor(selectedBooking.groupId) }"
          >
            <div
              class="group-badge"
              :style="{
                backgroundColor: getGroupColor(selectedBooking.groupId)
              }"
            >
              <i class="fas fa-users"></i>
              <span>Đặt vé nhóm #{{ selectedBooking.groupId }}</span>
            </div>

            <div class="group-members-container">
              <div v-if="isLoadingGroupMembers" class="loading-group-members">
                <i class="fas fa-spinner fa-spin"></i> Đang tải thành viên
                nhóm...
              </div>

              <div
                v-else-if="groupMembers.length === 0"
                class="no-group-members"
              >
                <i class="fas fa-info-circle"></i> Không có thành viên nhóm khác
              </div>

              <div v-else class="group-members">
                <div class="group-member-count">
                  <i class="fas fa-users"></i> {{ groupMembers.length }} thành
                  viên khác trong nhóm
                </div>
                <div class="group-member-list">
                  <div
                    v-for="member in groupMembers"
                    :key="member.id"
                    class="group-member-item"
                    :class="{ 'is-leader': member.cccd }"
                  >
                    <div class="member-name">
                      {{ member.customerName }}
                      <span v-if="member.cccd" class="leader-star-badge leader-star-sm" title="Trưởng nhóm">
                        ★
                      </span>
                    </div>
                    <div v-if="member.cccd" class="member-cccd">
                      <i class="fas fa-id-card"></i> {{ member.cccd }}
                      <span v-if="getBirthYearFromCccd(member.cccd)" class="member-birth-year">
                        &bull; {{ getBirthYearFromCccd(member.cccd) }}
                      </span>
                    </div>
                    <div class="member-info">
                      <span
                        class="member-seat"
                        v-if="!isSpecialTicketType(member.independent)"
                      >
                        <i class="fas fa-chair"></i>
                        {{ member.slotNumber || 'N/A' }}
                      </span>
                      <span
                        :class="[
                          'member-status',
                          member.status ? member.status.toLowerCase() : ''
                        ]"
                      >
                        {{
                          member.status ? translateStatus(member.status) : 'N/A'
                        }}
                      </span>
                    </div>
                    <button
                      class="view-member-btn"
                      @click="viewGroupMember(member)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="detail-section trip-info-section">
            <h3><i class="fas fa-route"></i> Thông tin chuyến đi</h3>

            <div class="ticket-highlight">
              <div
                class="trip-name"
                v-if="selectedBooking.tripInfo && selectedBooking.tripInfo.name"
              >
                <i class="fas fa-tag"></i>
                <strong>{{ selectedBooking.tripInfo.name }}</strong>
              </div>
              <div class="ticket-type">
                <i class="fas fa-ticket-alt"></i>
                <strong>{{ selectedBooking.ticketType }}</strong>
                <span v-if="isLoadingTicketDetails" class="loading-badge">
                  <i class="fas fa-spinner fa-spin"></i> Đang tải...
                </span>
                <span
                  v-else-if="isSpecialTicketType(selectedBooking.independent)"
                  class="special-badge"
                >
                  Vé đặc biệt
                </span>
              </div>
              <div class="ticket-price">
                <i class="fas fa-tag"></i>
                {{ formatCurrency(selectedBooking.ticketPrice) }}
              </div>
            </div>

            <div class="detail-grid">
              <div class="detail-group route-info">
                <label><i class="fas fa-map-marked-alt"></i> Tuyến đường</label>
                <div class="detail-value">{{ selectedBooking.route }}</div>
              </div>

              <div class="detail-group time-info">
                <label><i class="far fa-clock"></i> Thời gian khởi hành</label>
                <div class="detail-value">
                  {{ formatDate(selectedBooking.tripInfo.departure) }}
                </div>
              </div>

              <template
                v-if="!isSpecialTicketType(selectedBooking.independent)"
              >
                <div class="detail-group seat-info">
                  <label><i class="fas fa-chair"></i> Vị trí ghế đặt</label>
                  <div class="detail-value highlight-value">
                    <span class="seat-number">{{
                      selectedBooking.slotNumber || 'N/A'
                    }}</span>
                  </div>
                </div>

                <div
                  class="detail-group vehicle-info"
                  v-if="selectedBooking.vehicleInfo"
                >
                  <label><i class="fas fa-bus"></i> Xe</label>
                  <div class="detail-value">
                    <span class="vehicle-type">
                      <span
                        class="vehicle-badge"
                        :class="'vehicle-' + selectedBooking.vehicleInfo.type"
                      >
                        {{
                          formatVehicleType(selectedBooking.vehicleInfo.type)
                        }}
                      </span>
                    </span>
                    <span class="vehicle-plate">
                      <i class="fas fa-id-card"></i>
                      {{ selectedBooking.vehicleInfo.plateNumber }}
                    </span>
                  </div>
                </div>
              </template>

              <div
                class="detail-group special-note"
                v-if="isSpecialTicketType(selectedBooking.independent)"
              >
                <i class="fas fa-info-circle"></i>
                <div>
                  <strong>Vé đặc biệt:</strong>
                  <p>
                    Loại vé này không yêu cầu chọn ghế hoặc điểm đón. Khách hàng
                    Khách hàng sẽ tự túc đến điểm tham quan.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h3><i class="fas fa-user"></i> Thông tin khách hàng</h3>
            <div class="section-content">
              <div class="detail-grid">
                <div class="detail-group">
                  <label for="customer-name">Họ tên</label>
                  <div class="name-with-leader">
                    <input
                      id="customer-name"
                      v-model="selectedBooking.customerName"
                      type="text"
                      class="edit-input"
                    />
                    <span v-if="selectedBooking.cccd" class="leader-star-badge" title="Trưởng nhóm (đã CCCD)">
                      ★ Trưởng nhóm
                    </span>
                  </div>
                </div>
                <div class="detail-group">
                  <label for="customer-phone">Số điện thoại</label>
                  <input
                    id="customer-phone"
                    v-model="selectedBooking.customerPhone"
                    type="tel"
                    class="edit-input"
                  />
                </div>
                <div class="detail-group">
                  <label for="customer-email">Email</label>
                  <input
                    id="customer-email"
                    v-model="selectedBooking.customerEmail"
                    type="email"
                    class="edit-input"
                  />
                </div>
                <div class="detail-group" v-if="selectedBooking.cccd">
                  <label>CCCD Trưởng nhóm</label>
                  <div class="cccd-display">
                    <i class="fas fa-id-card"></i>
                    <span class="cccd-number">{{ selectedBooking.cccd }}</span>
                    <span v-if="selectedBooking.cccdAddress" class="cccd-address-small">{{ selectedBooking.cccdAddress }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h3><i class="fas fa-info-circle"></i> Thông tin bổ sung</h3>
            <div class="section-content">
              <div class="detail-grid">
                <div class="detail-group">
                  <label for="birth-year">Năm sinh</label>
                  <input
                    id="birth-year"
                    v-model="selectedBooking.birthYear"
                    type="number"
                    min="1900"
                    :max="new Date().getFullYear()"
                    class="edit-input"
                    :placeholder="selectedBooking.birthYear ? '' : (getBirthYearFromCccd(selectedBooking.cccd) ? `Dự đoán từ CCCD: ${getBirthYearFromCccd(selectedBooking.cccd)}` : 'Chưa có thông tin')"
                  />
                  <div v-if="!selectedBooking.birthYear && getBirthYearFromCccd(selectedBooking.cccd)" class="birth-year-hint">
                    ★ Năm sinh ước tính từ CCCD: <strong>{{ getBirthYearFromCccd(selectedBooking.cccd) }}</strong>
                  </div>
                </div>
                <div
                  class="detail-group"
                  v-if="!isSpecialTicketType(selectedBooking.independent)"
                >
                  <label for="pickup-location">Điểm đón</label>
                  <select
                    id="pickup-location"
                    v-model="selectedBooking.pickupLocationId"
                    class="edit-input"
                  >
                    <option value="">Chọn điểm đón</option>
                    <option
                      v-for="location in pickupLocations"
                      :key="location.pickup_location_id"
                      :value="location.pickup_location_id"
                    >
                      {{ location.location_name }} ({{ location.address }})
                    </option>
                  </select>
                </div>
                <div
                  class="detail-group"
                  v-if="
                    !isSpecialTicketType(selectedBooking.independent) &&
                    availableSeats.length > 0
                  "
                >
                  <label for="seat-number">
                    <i class="fas fa-chair"></i> Ghế ngồi
                  </label>
                  <select
                    id="seat-number"
                    v-model="selectedBooking.slotNumber"
                    class="edit-input"
                  >
                    <option :value="selectedBooking.slotNumber">
                      {{ selectedBooking.slotNumber }} (Hiện tại)
                    </option>
                    <option
                      v-for="seat in availableSeats"
                      :key="seat"
                      :value="seat"
                    >
                      {{ seat }}
                    </option>
                  </select>
                  <div class="help-text" v-if="availableSeats.length > 0">
                    <i class="fas fa-info-circle"></i>
                    {{ availableSeats.length }} ghế trống có sẵn
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h3><i class="fas fa-tasks"></i> Trạng thái đặt vé</h3>
            <div class="section-content">
              <div class="status-select-group">
                <select v-model="selectedBooking.status" class="status-select">
                  <option value="">Tất cả trạng thái</option>
                  <option value="pending">Chờ xác nhận</option>
                  <option value="confirmed">Đã xác nhận</option>
                  <option value="paid">Đã thanh toán</option>
                  <option value="paying">Đang thanh toán</option>
                  <option value="canceled">Đã hủy</option>
                  <option value="refund">Hoàn tiền</option>
                  <option value="exchange">Đổi vé</option>
                  <option value="reserved">Bảo lưu</option>
                  <option value="refund_90">Hoàn 90%</option>
                  <option value="refund_80">Hoàn 80%</option>
                  <option value="refund_70">Hoàn 70%</option>
                  <option value="refund_50">Hoàn 50%</option>
                  <option value="no_refund">Không hoàn vé</option>
                    <option value="contacted">Đã liên hệ</option>
                </select>
                <div class="status-help-text">
                  Chọn trạng thái để cập nhật đặt vé này
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Đóng</button>
          <button class="exchange-btn" @click="initiateTicketExchange">
            <i class="fas fa-exchange-alt"></i> Đổi vé
          </button>
          <button class="save-btn" @click="updateBookingStatus">
            <i class="fas fa-save"></i> Lưu thay đổi
          </button>
        </div>
      </div>
    </div>

    <!-- Exchange Ticket Modal -->
    <div
      class="modal"
      v-if="showExchangeModal"
      @click.self="closeExchangeModal"
    >
      <div class="modal-content exchange-modal">
        <div class="modal-header">
          <h2>Đổi vé mới cho khách hàng</h2>
          <button class="close-btn" @click="closeExchangeModal">×</button>
        </div>
        <div class="modal-body">
          <!-- Customer Info Summary (unchanged) -->
          <div class="customer-info-summary">
            <div class="customer-name">
              <i class="fas fa-user"></i> {{ exchangeBooking.customerName }}
            </div>
            <div class="customer-contact">
              <i class="fas fa-phone"></i> {{ exchangeBooking.customerPhone }}
              <span v-if="exchangeBooking.customerEmail">
                | <i class="fas fa-envelope"></i>
                {{ exchangeBooking.customerEmail }}
              </span>
            </div>
          </div>

          <!-- Multi-step Exchange Process -->
          <div class="exchange-process">
            <!-- Step 1: Route and Trip -->
            <div class="exchange-step">
              <div class="step-header">
                <span class="step-number">1</span>
                <h3>Chọn chuyến đi mới</h3>
              </div>

              <!-- Route Selection -->
              <div class="form-group">
                <label for="exchange-route"
                  ><i class="fas fa-map-marked-alt"></i> Tuyến đường:</label
                >
                <!-- Search Input -->
                <div class="route-search-wrapper">
                  <i class="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    v-model="routeSearchQuery"
                    class="route-search-input"
                    placeholder="Tìm kiếm tuyến đường..."
                    :disabled="isLoadingRoutes"
                  />
                  <button
                    v-if="routeSearchQuery"
                    @click="routeSearchQuery = ''"
                    class="clear-search-btn"
                    type="button"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <!-- Route Dropdown -->
                <select
                  id="exchange-route"
                  v-model="exchangeForm.routeId"
                  class="form-control"
                  @change="handleRouteChange"
                >
                  <option value="">
                    {{
                      isLoadingRoutes
                        ? 'Đang tải tuyến đường...'
                        : filteredAvailableRoutes.length === 0 && routeSearchQuery
                        ? 'Không tìm thấy tuyến đường'
                        : 'Chọn tuyến đường'
                    }}
                  </option>
                  <option
                    v-for="route in filteredAvailableRoutes"
                    :key="route.route_id"
                    :value="route.route_id"
                  >
                    {{ route.destination_location }}
                  </option>
                </select>
              </div>

              <!-- Trip Selection -->
              <div class="form-group">
                <label for="exchange-trip"
                  ><i class="fas fa-bus"></i> Chuyến đi:</label
                >
                <select
                  id="exchange-trip"
                  v-model="exchangeForm.tripId"
                  class="form-control"
                  @change="handleTripChange"
                  :disabled="!exchangeForm.routeId || isLoadingTrips"
                >
                  <option value="">
                    {{
                      isLoadingTrips
                        ? 'Đang tải chuyến đi...'
                        : 'Chọn chuyến đi'
                    }}
                  </option>
                  <option
                    v-for="trip in availableTrips"
                    :key="trip.trip_id"
                    :value="trip.trip_id"
                  >
                    {{ formatDate(trip.departure_datetime) }} -
                    {{ trip.name || 'Chuyến đi' }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Step 2: Ticket Type -->
            <div
              class="exchange-step"
              :class="{ 'step-disabled': !exchangeForm.tripId }"
            >
              <div class="step-header">
                <span class="step-number">2</span>
                <h3>Chọn loại vé</h3>
              </div>

              <div class="form-group">
                <label for="exchange-ticket-type"
                  ><i class="fas fa-ticket-alt"></i> Loại vé:</label
                >
                <select
                  id="exchange-ticket-type"
                  v-model="exchangeForm.ticketTypeId"
                  class="form-control"
                  @change="handleTicketTypeChange"
                  :disabled="!exchangeForm.tripId || isLoadingTicketTypes"
                >
                  <option value="">
                    {{
                      isLoadingTicketTypes
                        ? 'Đang tải loại vé...'
                        : 'Chọn loại vé'
                    }}
                  </option>
                  <option
                    v-for="ticket in availableTicketTypes"
                    :key="ticket.ticket_type_id"
                    :value="ticket.ticket_type_id"
                  >
                    {{ ticket.type }} - {{ formatCurrency(ticket.price) }}
                  </option>
                </select>

                <div class="price-info" v-if="exchangeForm.ticketTypeId">
                  <div class="price-comparison">
                    <div class="price-item">
                      <span>Giá vé cũ:</span>
                      <span>{{
                        formatCurrency(selectedBooking?.ticketPrice)
                      }}</span>
                    </div>
                    <div class="price-item">
                      <span>Giá vé mới:</span>
                      <span>{{ formatCurrency(exchangeNewPrice) }}</span>
                    </div>
                    <div
                      class="price-difference"
                      :class="
                        priceDifference > 0 ? 'price-increase' : 'price-refund'
                      "
                    >
                      <span>Chênh lệch:</span>
                      <span>
                        <i
                          :class="
                            priceDifference > 0
                              ? 'fas fa-arrow-up'
                              : 'fas fa-arrow-down'
                          "
                        ></i>
                        {{ formatCurrency(Math.abs(priceDifference)) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Seat Selection -->
            <div
              class="exchange-step"
              v-if="!isExchangeTicketIndependent"
              :class="{ 'step-disabled': !exchangeForm.ticketTypeId }"
            >
              <div class="step-header">
                <span class="step-number">3</span>
                <h3>Chọn ghế</h3>
              </div>

              <div class="form-group">
                <div v-if="isLoadingExchangeSeats" class="loading-indicator">
                  <i class="fas fa-spinner fa-spin"></i> Đang tải danh sách
                  ghế...
                </div>
                <div
                  v-else-if="exchangeForm.tripId && exchangeForm.ticketTypeId"
                >
                  <div
                    v-if="_exchangeAvailableSeats.length === 0"
                    class="no-seats-message"
                  >
                    <i class="fas fa-exclamation-triangle"></i> Không có ghế
                    trống cho chuyến này
                  </div>
                  <div v-else>
                    <div
                      class="vehicle-preview"
                      :class="getExchangeVehicleClass"
                    >
                      <div class="vehicle-label">
                        <i class="fas fa-bus"></i>
                        {{ formatVehicleType(getExchangeVehicleType) }}
                      </div>
                      <div class="seat-grid" :class="getExchangeVehicleClass">
                        <button
                          v-for="seat in _exchangeAvailableSeats"
                          :key="seat"
                          class="seat-button"
                          :class="{
                            'seat-selected': exchangeForm.seatNumber === seat
                          }"
                          @click="exchangeForm.seatNumber = seat"
                        >
                          {{ seat }}
                        </button>
                      </div>
                    </div>

                    <div
                      class="selected-seat-info"
                      v-if="exchangeForm.seatNumber"
                    >
                      <i class="fas fa-check-circle"></i> Ghế đã chọn:
                      <span class="selected-seat">{{
                        exchangeForm.seatNumber
                      }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="instruction-message">
                  <i class="fas fa-info-circle"></i> Vui lòng chọn loại vé trước
                </div>
              </div>
            </div>

            <!-- Step 4: Pickup Location -->
            <div
              class="exchange-step"
              v-if="!isExchangeTicketIndependent"
              :class="{ 'step-disabled': !exchangeForm.seatNumber }"
            >
              <div class="step-header">
                <span class="step-number">4</span>
                <h3>Chọn điểm đón</h3>
              </div>

              <div class="form-group">
                <label for="exchange-pickup"
                  ><i class="fas fa-map-marker-alt"></i> Điểm đón:</label
                >
                <select
                  id="exchange-pickup"
                  v-model="exchangeForm.pickupLocationId"
                  class="form-control"
                  :disabled="
                    !exchangeForm.seatNumber || isLoadingExchangePickups
                  "
                >
                  <option value="">
                    {{
                      isLoadingExchangePickups
                        ? 'Đang tải điểm đón...'
                        : 'Chọn điểm đón'
                    }}
                  </option>
                  <option
                    v-for="location in exchangePickupLocations"
                    :key="location.pickup_location_id"
                    :value="location.pickup_location_id"
                  >
                    {{ location.location_name }} ({{ location.address }})
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="cancel-btn" @click="closeExchangeModal">Hủy</button>
          <button
            class="exchange-confirm-btn"
            @click="confirmExchangeTicket"
            :disabled="!isExchangeFormValid || isProcessingExchange"
          >
            <i class="fas fa-spinner fa-spin" v-if="isProcessingExchange"></i>
            <i class="fas fa-check" v-else></i>
            Xác nhận đổi vé
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import { ToastService } from '@/services/ToastService'
import { callWithErrorHandling } from 'vue'

import * as XLSX from 'xlsx'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminBooking',
  components: {
    AdminSidebar
  },
  data () {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    )

    return {
      ticketTypeCache: {},
      isLoadingTicketDetails: false,
      showFilters: false,
      isDashboard: false,
      bookings: [],
      pickupLocations: [],
      isSidebarOpen: false,
      searchQuery: '',
      selectedBooking: null,
      statusFilter: '',
      currentUser: {
        full_name: 'Admin User',
        role: 'Administrator'
      },
      userRole: 'staff',
      dateRange: {
        from: null,
        to: null
      },
      currentFilter: 'Tất Cả',
      dateFilters: [
        { label: 'Tất Cả', value: 'all' },
        { label: 'Hôm Nay', value: 'today' },
        { label: 'Tuần Này', value: 'week' },
        { label: 'Tháng Này', value: 'month' },
        { label: 'Năm Nay', value: 'year' }
      ],
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      availableSeats: [],
      isLoadingSeats: false,
      groupMembers: [],
      groupColors: [
        '#3182ce', // Blue
        '#38a169', // Green
        '#805ad5', // Purple
        '#dd6b20', // Orange
        '#2c7a7b', // Teal
        '#e53e3e', // Red
        '#4a5568', // Gray
        '#d53f8c', // Pink
        '#d69e2e', // Yellow/Gold
        '#0987a0', // Cyan
        '#744210', // Brown
        '#4338ca', // Indigo
        '#EC4899', // Hot Pink
        '#10B981', // Emerald
        '#F97316', // Bright Orange
        '#8B5CF6', // Violet
        '#14B8A6', // Dark Teal
        '#F59E0B', // Amber
        '#EF4444', // Bright Red
        '#6366F1'  // Indigo Blue
      ],
      isLoadingGroupMembers: false,
      groupFilter: null,
      previousSearchState: null,
      showExchangeModal: false,
      exchangeBooking: null,
      exchangeForm: {
        routeId: '',
        tripId: '',
        ticketTypeId: '',
        seatNumber: '',
        pickupLocationId: ''
      },
      availableRoutes: [],
      routeSearchQuery: '',
      isLoadingRoutes: false,
      isProcessingExchange: false,
      exchangeNewPrice: 0,
      _exchangeAvailableSeats: [],
      isLoadingExchangeSeats: false,
      availableTrips: [],
      isLoadingTrips: false,
      availableTicketTypes: [],
      isLoadingTicketTypes: false,
      exchangePickupLocations: [],
      isLoadingExchangePickups: false,
      // Export feature
      showExportModal: false,
      isExporting: false,
      exportProgress: '',
      exportDateRange: {
        from: new Date().toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0]
      }
    }
  },
  computed: {
    userInitials () {
      return this.currentUser.full_name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },
    filteredBookings () {
      // Return bookings directly since filtering is now done by the API
      return this.bookings
    },
    totalPages () {
      return this.totalItems
        ? Math.ceil(this.totalItems / this.itemsPerPage)
        : 1
    },
    canExport () {
      return this.userRole === 'admin' || this.userRole === 'accountant'
    },
    isExchangeTicketIndependent () {
      if (
        !this.availableTicketTypes ||
        !this.availableTicketTypes.length ||
        !this.exchangeForm.ticketTypeId
      )
        return false
      const selectedTicket = this.availableTicketTypes.find(
        ticket => ticket.ticket_type_id === this.exchangeForm.ticketTypeId
      )
      return selectedTicket && selectedTicket.independent === true
    },

    isExchangeFormValid () {
      const form = this.exchangeForm
      if (!form.routeId || !form.tripId || !form.ticketTypeId) return false

      // For tickets with independent=true (special tickets), we don't need seat or pickup location
      if (this.isExchangeTicketIndependent) return true

      // For regular tickets, we need both seat and pickup location
      return !!form.seatNumber && !!form.pickupLocationId
    },

    exchangeAvailableSeats () {
      return this._exchangeAvailableSeats || []
    },
    priceDifference () {
      return (
        (this.exchangeNewPrice || 0) - (this.selectedBooking?.ticketPrice || 0)
      )
    },
    allExchangeSeats () {
      if (!this.exchangeForm.tripId) return []

      const vehicleType = this.getExchangeVehicleType
      let allSeats = []

      if (vehicleType === '16_slots') {
        allSeats = Array.from({ length: 16 }, (_, i) => `${i + 1}`)
      } else if (vehicleType === '28_slots') {
        allSeats = Array.from({ length: 28 }, (_, i) => `${i + 1}`)
      } else if (vehicleType === '45_slots') {
        allSeats = Array.from({ length: 45 }, (_, i) => `${i + 1}`)
      } else if (vehicleType === 'over_night') {
        const rows = ['A', 'B']
        for (let row of rows) {
          for (let i = 1; i <= 23; i++) {
            allSeats.push(`${i}${row}`)
          }
        }
      }

      return allSeats
    },

    getExchangeVehicleType () {
      if (!this.exchangeForm.tripId || !this.availableTrips.length)
        return '16_slots'

      const selectedTrip = this.availableTrips.find(
        trip => trip.trip_id === this.exchangeForm.tripId
      )

      return selectedTrip?.vehicle?.type || '16_slots'
    },

    getExchangeVehicleClass () {
      return `vehicle-type-${this.getExchangeVehicleType}`
    },

    filteredAvailableRoutes () {
      if (!this.routeSearchQuery) return this.availableRoutes

      const query = this.removeVietnameseAccents(this.routeSearchQuery)
      return this.availableRoutes.filter(route => {
        const destination = this.removeVietnameseAccents(route.destination_location)
        return destination.includes(query)
      })
    }
  },
  methods: {
    formatDob (birthDate, birthYear, cccd) {
      if (!birthDate && !birthYear && !cccd) return '—'
      
      let year = ''
      if (birthYear !== null && birthYear !== undefined && birthYear !== '') {
        const yrStr = String(birthYear).trim()
        if (yrStr && yrStr !== 'null' && yrStr !== 'undefined' && yrStr !== 'NaN') {
          year = yrStr
        }
      }

      if (!year && cccd) {
        const cccdStr = String(cccd).trim()
        if (cccdStr && cccdStr !== 'null' && cccdStr !== 'undefined' && cccdStr.length >= 6) {
          const centuryDigit = parseInt(cccdStr[3], 10)
          const yearDigits = parseInt(cccdStr.slice(4, 6), 10)
          if (!isNaN(centuryDigit) && !isNaN(yearDigits)) {
            const baseYear = centuryDigit <= 1 ? 1900 : 2000
            year = String(baseYear + yearDigits)
          }
        }
      }

      if (birthDate !== null && birthDate !== undefined && birthDate !== '') {
        const cleanDate = String(birthDate).trim()
        if (cleanDate && cleanDate !== 'null' && cleanDate !== 'undefined') {
          const yyyyMmDdPattern = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/
          const matchYmd = cleanDate.match(yyyyMmDdPattern)
          if (matchYmd) {
            const y = matchYmd[1]
            const m = matchYmd[2].padStart(2, '0')
            const d = matchYmd[3].padStart(2, '0')
            return `${d}/${m}/${y}`
          }
          const ddMmYyyyPattern = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/
          const matchDmy = cleanDate.match(ddMmYyyyPattern)
          if (matchDmy) {
            const d = matchDmy[1].padStart(2, '0')
            const m = matchDmy[2].padStart(2, '0')
            const y = matchDmy[3]
            return `${d}/${m}/${y}`
          }
          const ddMmPattern = /^(\d{1,2})[-/](\d{1,2})$/
          const matchDdMm = cleanDate.match(ddMmPattern)
          if (matchDdMm) {
            const d = matchDdMm[1].padStart(2, '0')
            const m = matchDdMm[2].padStart(2, '0')
            const y = year || '1900'
            return `${d}/${m}/${y}`
          }
          if (/^\d{4}$/.test(cleanDate)) {
            return `01/01/${cleanDate}`
          }
          return cleanDate
        }
      }
      
      if (year) {
        return `01/01/${year}`
      }
      return '—'
    },
    removeVietnameseAccents (str) {
      if (!str) return ''
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    clearGroupFilter () {
      this.groupFilter = null

      // Restore previous search state if available
      if (this.previousSearchState) {
        this.searchQuery = this.previousSearchState.query
        this.statusFilter = this.previousSearchState.status
        this.dateRange = this.previousSearchState.dateRange
        this.previousSearchState = null
      }

      this.fetchData()
    },
    showGroupBookings (groupId) {
      // Store the current search state to allow going back
      this.previousSearchState = {
        query: this.searchQuery,
        status: this.statusFilter,
        dateRange: { ...this.dateRange }
      }

      // Clear other filters and set group filter
      this.searchQuery = ''
      this.statusFilter = ''
      this.dateRange.from = null
      this.dateRange.to = null

      // Add a special parameter for the API
      this.groupFilter = groupId

      // Fetch the filtered data
      this.currentPage = 1
      this.fetchData()

      // Show notification
      ToastService.info(`Đang hiển thị tất cả vé trong nhóm #${groupId}`)
    },
    formatVehicleType (type) {
      const types = {
        '16_slots': 'Xe 16 chỗ',
        '28_slots': 'Xe 28 chỗ',
        '45_slots': 'Xe 45 chỗ',
        over_night: 'Xe giường nằm'
      }

      return types[type] || type
    },
    isSpecialTicketType (independent) {
      return independent === true
    },
    formatCurrency (amount) {
      if (!amount) return 'N/A'
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },
    openExportModal () {
      const today = new Date().toISOString().split('T')[0]
      this.exportDateRange = { from: today, to: today }
      this.exportProgress = ''
      this.showExportModal = true
    },
    getNextDay (dateStr) {
      const d = new Date(dateStr)
      d.setDate(d.getDate() + 1)
      return d.toISOString().split('T')[0]
    },
    translateStatusVi (status) {
      const map = {
        confirmed: 'Đã xác nhận',
        paid: 'Đã thanh toán',
        pending: 'Chờ xác nhận',
        paying: 'Đang thanh toán',
        canceled: 'Đã hủy',
        refund: 'Hoàn tiền',
        exchange: 'Đổi vé',
        reserved: 'Bảo lưu',
        refund_90: 'Hoàn 90%',
        refund_80: 'Hoàn 80%',
        refund_70: 'Hoàn 70%',
        refund_50: 'Hoàn 50%',
        no_refund: 'Không hoàn vé',
        contacted: 'Đã liên hệ',
      }
      return map[status] || status
    },
    getBirthYearFromCccdExport (cccd) {
      if (!cccd || cccd.length < 6) return ''
      const centuryDigit = parseInt(cccd[3], 10)
      const yearDigits = parseInt(cccd.slice(4, 6), 10)
      if (isNaN(centuryDigit) || isNaN(yearDigits)) return ''
      const baseYear = centuryDigit <= 1 ? 1900 : 2000
      return String(baseYear + yearDigits)
    },
    formatDateTimeVi (dt) {
      if (!dt) return ''
      const d = new Date(dt)
      const pad = n => String(n).padStart(2, '0')
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    async exportBookings () {
      if (!this.exportDateRange.from || !this.exportDateRange.to) return
      if (this.exportDateRange.from > this.exportDateRange.to) {
        alert('Ngày bắt đầu phải trước ngày kết thúc')
        return
      }

      this.isExporting = true
      this.exportProgress = 'Đang bắt đầu...'

      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Build time range: from 00:00:00 on start date to 00:00:00 on day AFTER end date
        const fromDateTime = `${this.exportDateRange.from} 00:00:00`
        const toDateTime = `${this.getNextDay(this.exportDateRange.to)} 00:00:00`

        let allBookings = []
        let page = 1
        let totalPages = 1

        // Paginate through all results to avoid missing anyone
        while (page <= totalPages) {
          this.exportProgress = totalPages > 1
            ? `Đang tải dữ liệu... (trang ${page}/${totalPages})`
            : `Đang tải dữ liệu...`

          const response = await axiosInstance.get(
            `${API_BASE_URL}/admin/bookings`,
            {
              headers,
              params: {
                page,
                per_page: 1000,
                from_date: fromDateTime,
                to_date: toDateTime
              }
            }
          )

          const items = response.data.items || []
          totalPages = response.data.pages || 1

          // Exclude canceled bookings
          const valid = items.filter(b => b.status !== 'canceled')
          allBookings.push(...valid)
          page++
        }

        if (allBookings.length === 0) {
          alert(`Không có đặt vé nào trong khoảng thời gian từ ${this.exportDateRange.from} đến ${this.exportDateRange.to}.`)
          this.isExporting = false
          return
        }

        // Sort ascending by booking_datetime
        allBookings.sort((a, b) =>
          new Date(a.booking_datetime) - new Date(b.booking_datetime)
        )

        this.exportProgress = `Đang tạo file Excel (${allBookings.length} đặt vé)...`
        await this.$nextTick()

        await this.generateExcel(allBookings)
      } catch (err) {
        console.error('Export error:', err)
        alert('Có lỗi khi xuất danh sách. Vui lòng thử lại.')
      } finally {
        this.isExporting = false
        this.exportProgress = ''
        this.showExportModal = false
      }
    },
    async generateExcel (bookings) {
      try {
        const ExcelJS = await import('exceljs')
        const workbook = new ExcelJS.default.Workbook()
        const ws = workbook.addWorksheet('Danh sách đặt vé')

        const headers = [
          'STT', 'Mã đặt vé', 'Họ tên', 'Số điện thoại', 'Email', 'CCCD', 'Địa chỉ CCCD',
          'Giới tính', 'Ngày sinh', 'Tuyến đường', 'Tên chuyến', 'Thời gian khởi hành',
          'Loại vé', 'Giá vé (VNĐ)', 'Mã giảm giá', 'Giảm giá (VNĐ)', 'Số Bút Toán/Mã tham chiếu', 'Tổng tiền đơn hàng', 'Số ghế', 'Điểm đón', 'Mã nhóm',
          'Trạng thái', 'Thời gian đặt', 'Biển số xe'
        ]
        
        ws.addRow(headers)

        // Set header style
        ws.getRow(1).eachCell(cell => {
          cell.font = { bold: true, color: { argb: 'FFFFFF' } }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F3864' } }
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
        })

        // Group booking_group_id to color logic
        const groupStyles = {}
        const uniqueGroups = [...new Set(bookings.map(b => b.booking_group_id).filter(id => id))]
        uniqueGroups.forEach((groupId, index) => {
          const colorIndex = index % this.groupColors.length
          groupStyles[groupId] = this.groupColors[colorIndex].replace('#', '')
        })

        // Calculate group totals for the export
        const groupTotals = {}
        bookings.forEach(b => {
          if (b.booking_group_id) {
            const price = b.ticket ? Number(b.ticket.price) : 0
            const discount = Number(b.discount_amount || 0)
            const finalPrice = price - discount
            groupTotals[b.booking_group_id] = (groupTotals[b.booking_group_id] || 0) + finalPrice
          }
        })

        bookings.forEach((b, i) => {
          const birthYear = b.birth_year || this.getBirthYearFromCccdExport(b.cccd)
          const pickupName = b.pickup_location
            ? `${b.pickup_location.location_name || ''}${b.pickup_location.address ? ' (' + b.pickup_location.address + ')' : ''}`
            : ''
          const isLeader = b.booking_group_id && b.cccd
          const displayName = isLeader ? `${b.customer_name} (*)` : b.customer_name

          // Standardize birth date display
          const dobDisplay = this.formatDob(b.birth_date || b.birthDate, b.birth_year || b.birthYear || birthYear, b.cccd)

          const row = ws.addRow([
            i + 1,
            b.booking_id,
            displayName,
            b.customer_phone,
            b.customer_email || '',
            b.cccd || '',
            b.cccd_address || '',
            b.gender || '',
            dobDisplay,
            b.route ? b.route.destination : '',
            b.trip ? b.trip.name : '',
            b.trip ? this.formatDateTimeVi(b.trip.departure_datetime) : '',
            b.ticket ? b.ticket.type : '',
            b.ticket ? Number(b.ticket.price) : 0,
            b.discount_code || '',
            b.discount_amount ? Number(b.discount_amount) : 0,
            b.transaction_reference || '',
            b.booking_group_id ? (isLeader ? groupTotals[b.booking_group_id] : '') : (b.ticket ? Number(b.ticket.price) - Number(b.discount_amount || 0) : 0),
            b.slot_number || '',
            pickupName,
            b.booking_group_id ? b.booking_group_id.slice(0, 8) : '',
            this.translateStatusVi(b.status),
            this.formatDateTimeVi(b.booking_datetime),
            b.vehicle ? b.vehicle.license_plate : ''
          ])

          let bgColor = 'FFFFFF'
          let textColor = '000000'
          
          if (b.booking_group_id && groupStyles[b.booking_group_id]) {
            bgColor = groupStyles[b.booking_group_id]
            // Calculate brightness to ensure text readability
            const r = parseInt(bgColor.slice(0, 2), 16)
            const g = parseInt(bgColor.slice(2, 4), 16)
            const b_color = parseInt(bgColor.slice(4, 6), 16)
            const brightness = (r * 299 + g * 587 + b_color * 114) / 1000
            textColor = brightness > 128 ? '000000' : 'FFFFFF'
          } else if (i % 2 === 1) { // Alternate row colors
            bgColor = 'F2F6FC'
          }

          row.eachCell(cell => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: bgColor }
            }
            cell.font = { color: { argb: textColor } }
            if (isLeader && cell._column._number === 3) { // Highlight the leader name specifically
               cell.font = { bold: true, color: { argb: textColor } }
            }
            if (b.booking_group_id) {
               cell.border = { 
                 top: { style: 'thin', color: { argb: 'CCCCCC' } },
                 bottom: { style: 'thin', color: { argb: 'CCCCCC' } },
                 left: { style: 'thin', color: { argb: 'CCCCCC' } },
                 right: { style: 'thin', color: { argb: 'CCCCCC' } }
               }
            }
          })
        })

        // Set column widths
        ws.columns = [
          { width: 5 }, { width: 12 }, { width: 25 }, { width: 15 }, { width: 28 },
          { width: 14 }, { width: 35 }, { width: 10 }, { width: 15 }, { width: 20 },
          { width: 20 }, { width: 22 }, { width: 20 }, { width: 16 }, { width: 14 },
          { width: 16 }, { width: 25 }, { width: 18 }, { width: 8 }, { width: 35 },
          { width: 12 }, { width: 16 }, { width: 20 }, { width: 12 }
        ]

        const fromStr = this.exportDateRange.from
        const toStr = this.exportDateRange.to
        const filename = `danh-sach-dat-ve_${fromStr}_den_${toStr}.xlsx`

        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (err) {
        console.error('Error generating Excel:', err)
        alert('Có lỗi khi tạo file Excel. Vui lòng thử lại.')
      }
    },
    /**
     * Dự đoán năm sinh từ CCCD Việt Nam (12 số)
     * cccd[3]: chữ số thế kỷ (0/1 → 1900s, 2/3 → 2000s)
     * cccd[4..5]: 2 chữ số năm sinh
     * Ví dụ: 034195... → century=1 (1900s), year=95 → 1995
     */
    getBirthYearFromCccd (cccd) {
      if (!cccd || cccd.length < 6) return null
      const centuryDigit = parseInt(cccd[3], 10)
      const yearDigits = parseInt(cccd.slice(4, 6), 10)
      if (isNaN(centuryDigit) || isNaN(yearDigits)) return null
      const baseYear = centuryDigit <= 1 ? 1900 : 2000
      return baseYear + yearDigits
    },
    async fetchBookings () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        let fromDate = null
        let toDate = null

        if (this.dateRange.from !== null && this.dateRange.to !== null) {
          fromDate = new Date(this.dateRange.from)
          fromDate.setHours(0, 0, 0, 0)
          toDate = new Date(this.dateRange.to)
          toDate.setHours(23, 59, 59, 999)
        }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/bookings`,
          {
            headers,
            params: {
              from_date: fromDate,
              to_date: toDate,
              page: this.currentPage,
              per_page: this.itemsPerPage,
              search: this.searchQuery || undefined,
              status: this.statusFilter || undefined,
              booking_group_id: this.groupFilter || undefined
            }
          }
        )

        this.bookings = (response.data.items || []).map(booking => {
          // Extract the ticket type ID to prefetch
          if (
            booking.ticket_type_id &&
            !this.ticketTypeCache[booking.ticket_type_id]
          ) {
            this.prefetchTicketTypeDetails(booking.ticket_type_id)
          }

          return {
            id: booking.booking_id,
            customerName: booking.customer_name,
            customerPhone: booking.customer_phone,
            customerEmail: booking.customer_email,
            route: booking.route.destination,
            routeId: booking.route.route_id,
            date: booking.booking_datetime,
            status: booking.status || 'pending',
            birthYear: booking.birth_year || null,
            cccd: booking.cccd || null,
            cccdAddress: booking.cccd_address || null,
            pickupLocationId: booking.pickup_location_id
              ? booking.pickup_location_id
              : null,
            slotNumber: booking.slot_number,
            tripId: booking.trip.trip_id,
            ticketTypeId: booking.ticket_type_id,
            ticketType: booking.ticket.type,
            ticketPrice: booking.ticket.price,
            groupId: booking.booking_group_id
              ? String(booking.booking_group_id)
              : null,
            tripInfo: booking.trip
              ? {
                  departure: booking.trip.departure_datetime,
                  name: booking.trip.name
                }
              : null,
            vehicleInfo: booking.vehicle
              ? {
                  type: booking.vehicle.type,
                  plateNumber: booking.vehicle.license_plate
                }
              : null,
            // Use cached independent flag if available
            independent:
              this.ticketTypeCache[booking.ticket_type_id]?.independent || false,
            invoice: booking.invoice || null,
          }
        })

        this.totalItems = response.data.total
      } catch (error) {
        console.error('Error fetching bookings:', error)
        if (error.response?.status === 401) {
          this.handleLogout()
        }
      }
    },
    getGroupColor (groupId) {
      if (!groupId) return ''

      // Use a more robust hash function for more consistent color assignment
      let hash = 0
      for (let i = 0; i < String(groupId).length; i++) {
        hash = String(groupId).charCodeAt(i) + ((hash << 5) - hash)
      }

      // Use the hash to get a consistent index
      const colorIndex = Math.abs(hash) % this.groupColors.length
      return this.groupColors[colorIndex]
    },

    async fetchGroupMembers () {
      if (!this.selectedBooking || !this.selectedBooking.groupId) {
        this.groupMembers = []
        return
      }

      try {
        this.isLoadingGroupMembers = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/bookings`,
          {
            headers,
            params: {
              booking_group_id: this.selectedBooking.groupId
            }
          }
        )

        // Filter out the current booking from group members
        this.groupMembers = response.data.items
          .filter(booking => booking.booking_id !== this.selectedBooking.id)
          .map(booking => ({
            id: booking.booking_id,
            customerName: booking.customer_name,
            slotNumber: booking.slot_number,
            status: booking.status || 'pending',
            independent: booking.ticket?.independent || false,
            cccd: booking.cccd || null,
            cccdAddress: booking.cccd_address || null,
            birthYear: booking.birth_year || null
          }))
      } catch (error) {
        console.error('Error fetching group members:', error)
        ToastService.error('Không thể tải thông tin thành viên nhóm')
        this.groupMembers = []
      } finally {
        this.isLoadingGroupMembers = false
      }
    },
    async prefetchTicketTypeDetails (ticketTypeId) {
      try {
        // Don't refetch if we already have it
        if (this.ticketTypeCache[ticketTypeId]) return

        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/ticket_types/${ticketTypeId}`,
          { headers }
        )

        if (response.data) {
          this.ticketTypeCache[ticketTypeId] = {
            independent: !!response.data.independent,
            type: response.data.type,
            price: response.data.price
          }
        }
      } catch (error) {
        console.error('Error prefetching ticket type details:', error)
      }
    },
    async fetchPickupLocations () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        console.log(
          'Fetching pickup locations for trip ID:',
          this.selectedBooking
        )

        const response = await axiosInstance.get(
          `${API_BASE_URL}/pickup_locations/`,
          {
            headers,
            params: { trip_id: this.selectedBooking.tripId, per_page: 1000 }
          }
        )

        console.log('Pickup locations response:', response.data)

        this.pickupLocations = response.data
      } catch (error) {
        console.error('Error fetching pickup locations:', error)
      }
    },
    toggleFilters () {
      this.showFilters = !this.showFilters
    },

    applySearch () {
      this.currentPage = 1
      this.fetchData() // Use fetchBookings or fetchDashboardData as appropriate
    },

    fetchData () {
      this.fetchBookings()
    },

    translateStatus (status) {
      const statusMap = {
        pending: 'Chờ xác nhận',
        confirmed: 'Đã xác nhận',
        paid: 'Đã thanh toán',
        paying: 'Đang thanh toán',
        canceled: 'Đã hủy',
        refund: 'Hoàn tiền',
        exchange: 'Đổi vé',
        reserved: 'Bảo lưu',
        refund_90: 'Hoàn 90%',
        refund_80: 'Hoàn 80%',
        refund_70: 'Hoàn 70%',
        refund_50: 'Hoàn 50%',
        no_refund: 'Không hoàn vé',
        contacted: 'Đã liên hệ',
      }

      return statusMap[status.toLowerCase()] || status
    },
    invoiceStatusLabel (status) {
      const map = { pending: 'Chờ duyệt', approved: 'Đã duyệt', issued: 'Đã xuất', rejected: 'Từ chối', failed: 'Lỗi' }
      return map[status] || '—'
    },
    handleLogout () {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('refreshToken')
      this.$router.push('/login')
    },
    formatDate (date) {
      if (!date) return '-'
      try {
        return new Date(date).toLocaleString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Asia/Ho_Chi_Minh'
        })
      } catch (error) {
        console.error('Date formatting error:', error)
        return '-'
      }
    },
    handleDateChange (type, event) {
      this.dateRange[type] = event.target.value
      this.currentFilter = null
    },
    applyQuickFilter (filterLabel) {
      this.currentFilter = filterLabel
      this.statusFilter = ''
      const today = new Date()

      switch (filterLabel) {
        case 'Hôm Nay':
          this.dateRange.from = today.toISOString().split('T')[0]
          this.dateRange.to = today.toISOString().split('T')[0]
          break
        case 'Tuần Này':
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay())
          )
          const lastDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay() + 6)
          )
          this.dateRange.from = firstDayOfWeek.toISOString().split('T')[0]
          this.dateRange.to = lastDayOfWeek.toISOString().split('T')[0]
          break
        case 'Tháng Này':
          const firstDayOfMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          )
          const lastDayOfMonth = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          )
          this.dateRange.from = firstDayOfMonth.toISOString().split('T')[0]
          this.dateRange.to = lastDayOfMonth.toISOString().split('T')[0]
          break
        case 'Năm Nay':
          const firstDayOfYear = new Date(today.getFullYear(), 0, 1)
          const lastDayOfYear = new Date(today.getFullYear(), 11, 31)
          this.dateRange.from = firstDayOfYear.toISOString().split('T')[0]
          this.dateRange.to = lastDayOfYear.toISOString().split('T')[0]
          break
      }

      this.fetchBookings()
    },
    changePage (newPage) {
      this.currentPage = newPage
      this.fetchBookings()
    },
    async openBookingModal (booking) {
      try {
        // If only ID is provided, fetch full booking details
        if (booking && booking.id && Object.keys(booking).length === 1) {
          const token = localStorage.getItem('adminToken')
          const headers = { Authorization: `Bearer ${token}` }

          const response = await axiosInstance.get(
            `${API_BASE_URL}/admin/bookings/${booking.id}`,
            { headers }
          )

          if (response.data) {
            booking = {
              id: response.data.booking_id,
              customerName: response.data.customer_name,
              customerPhone: response.data.customer_phone,
              customerEmail: response.data.customer_email || '',
              route: response.data.route.destination,
              routeId: response.data.route.route_id,
              date: response.data.booking_datetime,
              status: response.data.status || 'pending',
              birthYear: response.data.birth_year || null,
              cccd: response.data.cccd || null,
              cccdAddress: response.data.cccd_address || null,
              pickupLocationId: response.data.pickup_location_id || null,
              slotNumber: response.data.slot_number,
              tripId: response.data.trip.trip_id,
              ticketTypeId: response.data.ticket_type_id,
              ticketType: response.data.ticket?.type || '',
              ticketPrice: response.data.ticket?.price || 0,
              groupId: response.data.booking_group_id
                ? String(response.data.booking_group_id)
                : null,
              tripInfo: response.data.trip
                ? {
                    departure: response.data.trip.departure_datetime,
                    name: response.data.trip.name
                  }
                : null,
              vehicleInfo: response.data.vehicle
                ? {
                    type: response.data.vehicle.type,
                    plateNumber: response.data.vehicle.license_plate
                  }
                : null,
              independent: response.data.ticket?.independent || false
            }
          }
        }

        // Set initial loading state
        this.isLoadingTicketDetails = true

        // Check if we already have the ticket type cached
        const cachedTicketType = booking.ticketTypeId
          ? this.ticketTypeCache[booking.ticketTypeId]
          : null

        this.selectedBooking = {
          ...booking,
          pickupLocationId: booking.pickupLocationId
            ? Number(booking.pickupLocationId)
            : null,
          // Use cached independent value if available
          independent: cachedTicketType ? cachedTicketType.independent : false
        }

        // Determine immediately if pickup locations need to be fetched
        const shouldFetchPickupLocations = !this.isSpecialTicketType(
          this.selectedBooking.independent
        )

        const promises = []

        // Only fetch pickup locations if needed
        if (shouldFetchPickupLocations) {
          promises.push(
            this.fetchPickupLocations().catch(error => {
              console.error('Error fetching pickup locations:', error)
              this.pickupLocations = []
            })
          )

          // Also fetch available seats
          promises.push(
            this.fetchAvailableSeats(this.selectedBooking.tripId).catch(
              error => {
                console.error('Error fetching available seats:', error)
              }
            )
          )
        }

        // Only fetch ticket details if not already cached
        if (this.selectedBooking.ticketTypeId && !cachedTicketType) {
          promises.push(
            this.fetchTicketTypeDetails(this.selectedBooking.ticketTypeId)
          )
        }

        if (this.selectedBooking.groupId) {
          promises.push(this.fetchGroupMembers())
        }

        await Promise.all(promises)

        // Rest of the function remains the same
        // ...
      } catch (error) {
        console.error('Error opening booking modal:', error)
        ToastService.error('Có lỗi khi mở thông tin đặt vé. Vui lòng thử lại.')
      } finally {
        this.isLoadingTicketDetails = false
      }
    },
    async fetchTicketTypeDetails (ticketTypeId) {
      if (!this.selectedBooking) return

      try {
        // Use cache if available
        if (this.ticketTypeCache[ticketTypeId]) {
          const cachedData = this.ticketTypeCache[ticketTypeId]
          this.selectedBooking.independent = cachedData.independent
          this.selectedBooking.ticketTypeName = cachedData.type
          this.selectedBooking.ticketPrice = cachedData.price
          return // Exit early since we used the cache
        }

        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/ticket_types/${ticketTypeId}`,
          { headers }
        )

        if (this.selectedBooking && response.data) {
          const independent = !!response.data.independent
          this.selectedBooking.independent = independent
          this.selectedBooking.ticketTypeName = response.data.type
          this.selectedBooking.ticketPrice = response.data.price

          // Save to cache for future use
          this.ticketTypeCache[ticketTypeId] = {
            independent,
            type: response.data.type,
            price: response.data.price
          }
        }
      } catch (error) {
        console.error('Error fetching ticket type details:', error)
      }
    },
    async fetchAvailableSeats (tripId) {
      if (
        !tripId ||
        this.isSpecialTicketType(this.selectedBooking?.independent)
      ) {
        this.availableSeats = []
        return
      }

      try {
        this.isLoadingSeats = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        console.log('Fetching available seats for trip ID:', tripId)

        // Get trip details
        const tripResponse = await axiosInstance.get(
          `${API_BASE_URL}/admin/trips/`,
          { headers, params: { trip_id: tripId } }
        )

        console.log('Trip response:', tripResponse.data.items)

        const tripData = tripResponse.data.items[0]
        const vehicleType = tripData.vehicle.type
        const lockedSeats = tripData.locked_seats || []

        // Get all booked seats for this trip
        const bookingsResponse = await axiosInstance.get(
          `${API_BASE_URL}/bookings/`,
          {
            headers,
            params: {
              trip_id: tripId
            }
          }
        )

        console.log('Bookings response:', bookingsResponse.data)

        // Create array of all possible seats based on vehicle type
        let allSeats = []
        let totalSeats = 0

        // Get total capacity based on vehicle type
        if (vehicleType === '16_slots') totalSeats = 16
        else if (vehicleType === '28_slots') totalSeats = 28
        else if (vehicleType === '45_slots') totalSeats = 45

        if (
          vehicleType === '16_slots' ||
          vehicleType === '28_slots' ||
          vehicleType === '45_slots'
        ) {
          allSeats = Array.from({ length: totalSeats }, (_, i) =>
            (i + 1).toString()
          )
        } else if (vehicleType === 'over_night') {
          // For sleeper buses with format like 1A, 1B, 2A, 2B...
          const rows = ['A', 'B']
          rows.forEach(row => {
            for (let i = 1; i <= 23; i++) {
              allSeats.push(`${i}${row}`)
            }
          })
        }

        // Get booked seats (excluding the current booking's seat if editing)
        const currentBookingId = this.selectedBooking
          ? this.selectedBooking.id
          : null
        // Statuses that release their seat (must match backend SEAT_RELEASING_STATUSES)
        const SEAT_RELEASING_STATUSES = [
          'canceled', 'refund', 'exchange', 'reserved',
          'refund_90', 'refund_80', 'refund_70', 'refund_50', 'no_refund',
          'contacted'
        ]
        const bookedSeats = bookingsResponse.data
          .filter(
            b =>
              b.booking_id !== currentBookingId &&
              !SEAT_RELEASING_STATUSES.includes(b.status)
          )
          .map(b => b.slot_number)

        // Get held seats from the booking data
        const heldSeats = bookingsResponse.data
          .filter(b => b.status === 'held')
          .map(b => b.slot_number)

        // Filter out booked seats, locked seats, and held seats to get available seats
        this.availableSeats = allSeats.filter(
          seat =>
            !bookedSeats.includes(seat) &&
            !lockedSeats.includes(seat) &&
            !heldSeats.includes(seat)
        )

        console.log(
          `Available seats: ${this.availableSeats.length} out of ${allSeats.length}`,
          `(Booked: ${bookedSeats.length}, Locked: ${lockedSeats.length}, Held: ${heldSeats.length})`
        )
      } catch (error) {
        console.error('Error fetching available seats:', error)
        ToastService.error('Không thể tải danh sách ghế trống')
        this.availableSeats = []
      } finally {
        this.isLoadingSeats = false
      }
    },
    async fetchExchangeAvailableSeats () {
      if (!this.exchangeForm.tripId || this.isExchangeTicketIndependent) {
        this._exchangeAvailableSeats = []
        return
      }

      try {
        this.isLoadingExchangeSeats = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Get trip details
        const tripResponse = await axiosInstance.get(
          `${API_BASE_URL}/admin/trips/`,
          {
            headers,
            params: { trip_id: this.exchangeForm.tripId }
          }
        )

        const tripData = tripResponse.data.items[0]
        const vehicleType = tripData.vehicle.type

        // Get all bookings for this trip
        const bookingsResponse = await axiosInstance.get(
          `${API_BASE_URL}/bookings/`,
          {
            headers,
            params: {
              trip_id: this.exchangeForm.tripId
            }
          }
        )

        // Generate all seats based on vehicle type
        let allSeats = []
        let totalSeats = 0

        // Determine total seats based on vehicle type
        if (vehicleType === '16_slots') totalSeats = 16
        else if (vehicleType === '28_slots') totalSeats = 28
        else if (vehicleType === '45_slots') totalSeats = 45

        if (
          vehicleType === '16_slots' ||
          vehicleType === '28_slots' ||
          vehicleType === '45_slots'
        ) {
          allSeats = Array.from({ length: totalSeats }, (_, i) =>
            (i + 1).toString()
          )
        } else if (vehicleType === 'over_night') {
          // For sleeper buses with format like 1A, 1B, 2A, 2B...
          const rows = ['A', 'B']
          rows.forEach(row => {
            for (let i = 1; i <= 23; i++) {
              allSeats.push(`${i}${row}`)
            }
          })
        }

        // Filter out booked seats
        const currentBookingId = this.selectedBooking
          ? this.selectedBooking.id
          : null
        const SEAT_RELEASING_STATUSES = [
          'canceled', 'refund', 'exchange', 'reserved',
          'refund_90', 'refund_80', 'refund_70', 'refund_50', 'no_refund',
          'contacted'
        ]
        const bookedSeats = bookingsResponse.data
          .filter(
            booking =>
              booking.booking_id !== currentBookingId &&
              !SEAT_RELEASING_STATUSES.includes(booking.status) &&
              booking.slot_number !== 'special'
          )
          .map(booking => booking.slot_number)

        // Set available seats (just like in availableSeats computed property)
        this._exchangeAvailableSeats = allSeats.filter(
          seat => !bookedSeats.includes(seat)
        )

        console.log(
          `Available seats: ${this._exchangeAvailableSeats.length} out of ${allSeats.length}`,
          `(Booked: ${bookedSeats.length})`
        )
      } catch (error) {
        console.error('Error fetching available seats:', error)
        ToastService.error('Không thể tải danh sách ghế trống')
      } finally {
        this.isLoadingExchangeSeats = false
      }
    },
    closeModal () {
      this.selectedBooking = null
    },
    async updateBookingStatus () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const updatedData = {
          status: this.selectedBooking.status,
          customer_name: this.selectedBooking.customerName,
          customer_phone: this.selectedBooking.customerPhone,
          customer_email: this.selectedBooking.customerEmail,
          birth_year: this.selectedBooking.birthYear
            ? Number(this.selectedBooking.birthYear)
            : null,
          pickup_location_id: this.selectedBooking.pickupLocationId
            ? Number(this.selectedBooking.pickupLocationId)
            : null,
          // Add slot_number to the update data
          slot_number: this.selectedBooking.slotNumber
        }

        const response = await axiosInstance.put(
          `${API_BASE_URL}/admin/bookings/${this.selectedBooking.id}`,
          updatedData,
          { headers }
        )

        // Update local booking data with edited values
        const bookingIndex = this.bookings.findIndex(
          b => b.id === this.selectedBooking.id
        )
        if (bookingIndex !== -1) {
          this.bookings[bookingIndex] = {
            ...this.bookings[bookingIndex],
            customerName: this.selectedBooking.customerName,
            customerPhone: this.selectedBooking.customerPhone,
            customerEmail: this.selectedBooking.customerEmail,
            status: this.selectedBooking.status,
            birthYear: this.selectedBooking.birthYear,
            pickupLocationId: this.selectedBooking.pickupLocationId || null,
            pickupLocation: this.pickupLocations.find(
              loc =>
                loc.pickup_location_id === this.selectedBooking.pickupLocationId
            )
              ? `${
                  this.pickupLocations.find(
                    loc =>
                      loc.pickup_location_id ===
                      this.selectedBooking.pickupLocationId
                  ).location_name
                } (${
                  this.pickupLocations.find(
                    loc =>
                      loc.pickup_location_id ===
                      this.selectedBooking.pickupLocationId
                  ).address
                })`
              : 'Không xác định'
          }
        }

        // Show success message
        ToastService.success('Cập nhật thành công')
        this.closeModal()

        // Refresh data
        this.fetchData()
      } catch (error) {
        console.error('Error updating booking:', error)
        const errorMsg =
          error.response?.data?.detail ||
          error.message ||
          'Có lỗi khi cập nhật. Vui lòng thử lại.'
        ToastService.error(errorMsg)
      }
    },
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    async viewGroupMember (member) {
      try {
        // Show loading state
        ToastService.info('Đang tải thông tin đặt vé...')

        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Fetch the complete booking data using the ID
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/bookings/${member.id}`,
          { headers }
        )

        if (response.data) {
          // Close current modal
          this.closeModal()

          // Format the booking data properly
          const booking = {
            id: response.data.booking_id,
            customerName: response.data.customer_name,
            customerPhone: response.data.customer_phone,
            customerEmail: response.data.customer_email || '',
            route: response.data.route.destination,
            routeId: response.data.route.route_id,
            date: response.data.booking_datetime,
            status: response.data.status || 'pending',
            birthYear: response.data.birth_year || null,
            cccd: response.data.cccd || null,
            cccdAddress: response.data.cccd_address || null,
            pickupLocationId: response.data.pickup_location_id || null,
            slotNumber: response.data.slot_number,
            tripId: response.data.trip.trip_id,
            ticketTypeId: response.data.ticket_type_id,
            ticketType: response.data.ticket?.type || '',
            ticketPrice: response.data.ticket?.price || 0,
            groupId: response.data.booking_group_id
              ? String(response.data.booking_group_id)
              : null,
            tripInfo: response.data.trip
              ? {
                  departure: response.data.trip.departure_datetime,
                  name: response.data.trip.name
                }
              : null,
            vehicleInfo: response.data.vehicle
              ? {
                  type: response.data.vehicle.type,
                  plateNumber: response.data.vehicle.license_plate
                }
              : null
          }

          // Open the modal with complete data
          this.openBookingModal(booking)
        }
      } catch (error) {
        console.error('Error fetching group member details:', error)
        ToastService.error('Không thể tải thông tin đặt vé. Vui lòng thử lại.')
      }
    },
    initiateTicketExchange () {
      // Clone customer information to exchangeBooking
      this.exchangeBooking = {
        customerName: this.selectedBooking.customerName,
        customerPhone: this.selectedBooking.customerPhone,
        customerEmail: this.selectedBooking.customerEmail,
        birthYear: this.selectedBooking.birthYear,
        id: this.selectedBooking.id
      }

      // Reset exchange form
      this.exchangeForm = {
        routeId: '',
        tripId: '',
        ticketTypeId: '',
        seatNumber: '',
        pickupLocationId: ''
      }

      // Reset available data
      this._exchangeAvailableSeats = []
      this.exchangePickupLocations = []
      this.availableTicketTypes = []
      this.availableTrips = []
      this.routeSearchQuery = ''

      // Load available routes
      this.fetchAvailableRoutes()

      // Show exchange modal
      this.showExchangeModal = true
    },

    closeExchangeModal () {
      this.showExchangeModal = false
      this.exchangeBooking = null
      this.routeSearchQuery = ''
    },

    async fetchAvailableRoutes () {
      try {
        this.isLoadingRoutes = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Lấy tất cả routes đang active từ hôm nay đến hết năm
        const today = new Date()
        const endOfYear = new Date(today.getFullYear(), 11, 31) // 31 tháng 12
        
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/routes/`,
          { 
            headers,
            params: {
              is_active: true, // Lọc theo trạng thái active
              from_date: today.toISOString().split('T')[0], // Format: YYYY-MM-DD
              to_date: endOfYear.toISOString().split('T')[0], // Format: YYYY-MM-DD
              per_page: 1000 // Lấy tất cả routes
            }
          }
        )
        this.availableRoutes = response.data || []
      } catch (error) {
        console.error('Error fetching routes:', error)
        ToastService.error('Không thể tải danh sách tuyến đường.')
      } finally {
        this.isLoadingRoutes = false
      }
    },

    async handleRouteChange () {
      // Reset related fields
      this.exchangeForm.tripId = ''
      this.exchangeForm.ticketTypeId = ''
      this.exchangeForm.seatNumber = ''
      this.exchangeForm.pickupLocationId = ''
      this.availableTrips = []
      this.availableTicketTypes = []
      this._exchangeAvailableSeats = []
      this.exchangePickupLocations = []

      if (!this.exchangeForm.routeId) return

      try {
        this.isLoadingTrips = true
        console.log('Fetching trips for route ID:', this.exchangeForm.routeId)
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Lấy ngày hiện tại để lọc chuyến đi từ hôm nay trở đi
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Set về đầu ngày
        const fromDate = today.toISOString().split('T')[0]

        // Fetch available trips for selected route (từ hôm nay trở đi)
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/trips/`,
          {
            headers,
            params: {
              route_id: this.exchangeForm.routeId,
              status: 'scheduled',
              per_page: 1000, // Lấy tất cả chuyến
              order_by: 'asc'
            }
          }
        )

        // Make sure to check for items property in the response
        this.availableTrips = response.data.items || []

        // Log for debugging
        console.log('Available trips loaded:', this.availableTrips)

        if (this.availableTrips.length === 0) {
          ToastService.info('Không tìm thấy chuyến đi nào cho tuyến đường này')
        }
      } catch (error) {
        console.error('Error fetching trips:', error)
        ToastService.error('Không thể tải danh sách chuyến đi.')
      } finally {
        this.isLoadingTrips = false
        console.log(
          this.availableTrips.length > 0
            ? 'Available trips loaded successfully'
            : 'No available trips found'
        )
      }
    },

    async handleTripChange () {
      // Reset related fields
      this.exchangeForm.ticketTypeId = ''
      this.exchangeForm.seatNumber = ''
      this.exchangeForm.pickupLocationId = ''
      this.availableTicketTypes = []
      this._exchangeAvailableSeats = []
      this.exchangePickupLocations = []

      if (!this.exchangeForm.tripId) return

      try {
        this.isLoadingTicketTypes = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Fetch ticket types for selected trip
        const response = await axiosInstance.get(
          `${API_BASE_URL}/ticket_types/`,
          {
            headers,
            params: { trip_id: this.exchangeForm.tripId, per_page: 1000 }
          }
        )

        this.availableTicketTypes = response.data || []

        // Fetch pickup locations
        this.fetchExchangePickupLocations()
      } catch (error) {
        console.error('Error fetching ticket types:', error)
        ToastService.error('Không thể tải danh sách loại vé.')
      } finally {
        this.isLoadingTicketTypes = false
      }
    },

    async handleTicketTypeChange () {
      // Reset seat selection
      this.exchangeForm.seatNumber = ''
      this._exchangeAvailableSeats = []

      if (!this.exchangeForm.ticketTypeId) return

      // Get ticket price and info
      const selectedTicket = this.availableTicketTypes.find(
        ticket => ticket.ticket_type_id === this.exchangeForm.ticketTypeId
      )

      if (selectedTicket) {
        this.exchangeNewPrice = selectedTicket.price
      }

      // If not a special ticket, fetch available seats
      if (!this.isExchangeTicketIndependent) {
        this.fetchExchangeAvailableSeats()
      } else {
        // For special tickets, we use "special" as seat number
        this.exchangeForm.seatNumber = 'special'
      }
    },

    async fetchExchangePickupLocations () {
      if (!this.exchangeForm.tripId) return

      try {
        this.isLoadingExchangePickups = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/pickup_locations/`,
          {
            headers,
            params: { trip_id: this.exchangeForm.tripId, per_page: 1000 }
          }
        )

        this.exchangePickupLocations = response.data || []
      } catch (error) {
        console.error('Error fetching pickup locations:', error)
        ToastService.error('Không thể tải danh sách điểm đón.')
      } finally {
        this.isLoadingExchangePickups = false
      }
    },

    async confirmExchangeTicket () {
      if (!this.isExchangeFormValid || this.isProcessingExchange) return

      // Confirm with user
      if (
        !confirm(
          'Bạn có chắc chắn muốn đổi vé cho khách hàng này? Thao tác này không thể hoàn tác.'
        )
      ) {
        return
      }

      try {
        this.isProcessingExchange = true

        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Sửa tên trường để khớp với schema của backend
        const exchangeData = {
          booking_id: this.exchangeBooking.id,
          new_trip_id: this.exchangeForm.tripId, // Thêm "new_"
          new_ticket_type_id: this.exchangeForm.ticketTypeId, // Thêm "new_"
          new_slot_number: this.exchangeForm.seatNumber, // Thêm "new_"
          new_pickup_location_id: this.isExchangeTicketIndependent // Thêm "new_"
            ? null
            : this.exchangeForm.pickupLocationId,
          customer_name: this.exchangeBooking.customerName,
          customer_phone: this.exchangeBooking.customerPhone,
          customer_email: this.exchangeBooking.customerEmail,
          birth_year: this.exchangeBooking.birthYear
        }

        // Call the API to exchange ticket
        const response = await axiosInstance.post(
          `${API_BASE_URL}/admin/bookings/exchange`,
          exchangeData,
          { headers }
        )

        // Show success message
        ToastService.success('Đổi vé thành công cho khách hàng')

        // Close modals
        this.closeExchangeModal()
        this.closeModal()

        // Refresh booking list
        this.fetchData()
      } catch (error) {
        console.error('Error exchanging ticket:', error)

        if (error.response?.status === 409) {
          ToastService.error(
            'Không thể đổi vé. Ghế đã được đặt hoặc có xung đột với đặt vé khác.'
          )
        } else if (error.response?.status === 422) {
          // Chi tiết lỗi từ backend
          const errorDetails = error.response.data.detail
          if (Array.isArray(errorDetails) && errorDetails.length > 0) {
            ToastService.error(`Lỗi dữ liệu: ${errorDetails[0].msg}`)
          } else {
            ToastService.error(
              'Dữ liệu đổi vé không hợp lệ. Vui lòng kiểm tra lại.'
            )
          }
        } else {
          ToastService.error('Có lỗi khi đổi vé. Vui lòng thử lại.')
        }
      } finally {
        this.isProcessingExchange = false
      }
    }
  },
  mounted () {
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) this.isSidebarOpen = sidebarState === 'true'
    // Parse role from JWT token
    try {
      const token = localStorage.getItem('adminToken')
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]))
        this.userRole = payload.role || 'staff'
      }
    } catch (e) { /* ignore */ }
    this.fetchBookings()
  }
}
</script>

<style scoped>
@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';

.admin-layout {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.trip-info-section {
  border-radius: 8px;
  background-color: #f9f9f9;
  padding: 20px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.ticket-highlight {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 15px;
  background: linear-gradient(to right, #f0f8ff, #eaf5ff);
  border-left: 4px solid #4a90e2;
  border-radius: 4px;
}

.ticket-type {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
}

.ticket-type i {
  color: #4a90e2;
}

.ticket-price {
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ticket-price i {
  color: #28a745;
}

.special-badge {
  font-size: 0.8rem;
  background-color: #ff7043;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.detail-group {
  margin-bottom: 0;
}

.detail-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 8px;
}

.detail-group label i {
  width: 16px;
  text-align: center;
}

.detail-value {
  font-weight: 500;
  color: #2c3e50;
  padding: 8px 12px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #eaeaea;
}

.highlight-value {
  font-size: 18px;
  font-weight: 600;
  color: #4a90e2;
}

.vehicle-info .detail-value {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vehicle-plate {
  color: #6c757d;
  font-size: 0.9em;
}

.special-note {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  background-color: #fff3cd;
  border-radius: 4px;
  color: #856404;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .ticket-highlight {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
.vehicle-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  color: white;
  background-color: #4a90e2;
  margin-bottom: 6px;
}

.vehicle-16_slots {
  background-color: #4a90e2;
}

.vehicle-28_slots {
  background-color: #47b881;
}

.vehicle-45_slots {
  background-color: #7e57c2;
}

.vehicle-over_night {
  background-color: #f6ad55;
}

.vehicle-plate {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4a5568;
  font-size: 0.9rem;
  padding: 2px 0;
}

.detail-value {
  font-weight: 500;
  color: #2c3e50;
  padding: 8px 12px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
}

.vehicle-info .detail-value {
  padding: 10px 12px;
}

.seat-number {
  font-size: 18px;
  font-weight: 600;
  color: #4a90e2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 0;
}

.highlight-value {
  text-align: center;
  background-color: #f0f8ff;
  border: 1px solid #cce5ff;
}
.detail-section {
  margin-bottom: 24px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid #edf2f7;
  transition: all 0.3s ease;
}

.detail-section:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.detail-section h3 {
  padding: 16px 20px;
  margin: 0;
  background: linear-gradient(to right, #f8f9fa, #ffffff);
  border-bottom: 1px solid #edf2f7;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-section h3 i {
  color: #4299e1;
  font-size: 18px;
}

/* Container cho nội dung section */
.section-content {
  padding: 20px;
}

/* Cải thiện các input field */
.edit-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  background-color: #f8fafc;
}

.edit-input:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  background-color: #ffffff;
  outline: none;
}

.edit-input:hover {
  border-color: #cbd5e0;
}

select.edit-input {
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  padding-right: 32px;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 500;
  font-size: 0.85rem;
  display: inline-block; /* Changed from flex to allow proper wrapping */
  line-height: 1.4;
  text-align: center;
  white-space: normal; /* Allow text to wrap */
  word-break: normal;
  width: auto; /* Allow the width to adjust to content */
  min-width: 100px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Apply the same status color classes for badges */
.status-badge.pending {
  background-color: #fffbeb;
  color: #92400e;
  border: 1px solid #fef3c7;
}

.status-badge.confirmed {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #d1fae5;
}

.status-badge.paid {
  background-color: #eff6ff;
  color: #1e40af;
  border: 1px solid #dbeafe;
}

.status-badge.paying {
  background-color: #f0f9ff;
  color: #0c4a6e;
  border: 1px solid #e0f2fe;
}

.status-badge.canceled {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fee2e2;
}

.status-badge.refund,
.booking-status.refund {
  background-color: #fce7f3;
  color: #be185d;
  border: 1px solid #fbcfe8;
}

.status-badge.exchange,
.booking-status.exchange {
  background-color: #f5f3ff;
  color: #5b21b6;
  border: 1px solid #ede9fe;
}

.status-badge.reserved,
.booking-status.reserved {
  background-color: #e0f2fe;
  color: #0369a1;
  border: 1px solid #bae6fd;
}

.status-badge.refund_90,
.status-badge.refund_80,
.status-badge.refund_70,
.status-badge.refund_50,
.booking-status.refund_90,
.booking-status.refund_80,
.booking-status.refund_70,
.booking-status.refund_50 {
  background-color: #fce7f3;
  color: #be185d;
  border: 1px solid #fbcfe8;
}

.status-badge.no_refund,
.booking-status.no_refund {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fee2e2;
}

.status-badge.contacted,
.booking-status.contacted {
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

/* Adjust the status cell to properly contain the badge */
.status {
  white-space: normal;
  min-width: 120px;
}

.booking-status {
  padding: 8px 14px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
  display: inline-block; /* Changed from flex to inline-block */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  width: auto; /* Allow width to fit content */
  line-height: 1.4; /* Improve line height for wrapped text */
  word-break: normal; /* Allow proper word breaking */
}

.pending {
  background-color: #fffbeb;
  color: #92400e;
  border: 1px solid #fef3c7;
}

.confirmed {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #d1fae5;
}

.paid {
  background-color: #eff6ff;
  color: #1e40af;
  border: 1px solid #dbeafe;
}

.paying {
  background-color: #f0f9ff;
  color: #0c4a6e;
  border: 1px solid #e0f2fe;
}

.canceled {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fee2e2;
}

/* Cải thiện status select group */
.status-select-group {
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.status-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  padding-right: 36px;
  transition: all 0.2s;
  background-color: #ffffff;
  cursor: pointer;
}

.status-select:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  outline: none;
}

.status-help-text {
  color: #718096;
  font-size: 0.85rem;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-help-text:before {
  content: '\f05a';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  color: #718096;
}

/* Nâng cấp vé đặc biệt */
.special-note {
  grid-column: 1 / -1;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: #fff8ef;
  border-radius: 8px;
  color: #9a3412;
  margin-top: 16px;
  border-left: 4px solid #fb923c;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.special-note i {
  font-size: 18px;
  color: #ea580c;
}

/* Cải thiện modal */
.modal {
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}

.modal-content {
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  max-width: 800px;
  width: 90%;
  animation: modal-slide-in 0.3s ease-out forwards;
  border: none;
}

@keyframes modal-slide-in {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  background: linear-gradient(to right, #4299e1, #63b3ed);
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
}

.modal-header h2 {
  color: white;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  height: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 24px;
  background-color: #f8fafc;
}

.modal-footer {
  padding: 16px 24px;
  background-color: #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e2e8f0;
}

/* Nút lưu và đóng */
.save-btn,
.cancel-btn,
.exchange-btn {
  padding: 10px 18px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: none;
  cursor: pointer;
}

.save-btn {
  background-color: #3182ce;
  color: white;
}

.save-btn:hover {
  background-color: #2c5282;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.cancel-btn {
  background-color: #e2e8f0;
  color: #4a5568;
}

.cancel-btn:hover {
  background-color: #cbd5e0;
}

.exchange-btn {
  background-color: #f6ad55;
  color: white;
}

.exchange-btn:hover {
  background-color: #dd6b20;
}

/* Booking summary */
.booking-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.booking-id-badge {
  background-color: #ebf4ff;
  color: #3182ce;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid #bee3f8;
  display: flex;
  align-items: center;
}

.booking-id-badge:before {
  content: '\f02a';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  margin-right: 8px;
  font-size: 0.9rem;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  background-color: #f8fafc;
  appearance: none;
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  padding-right: 30px;
  width: 100%;
}

.filter-select:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  background-color: #ffffff;
  outline: none;
}

.filter-select:hover {
  border-color: #cbd5e0;
}

.loading-badge {
  font-size: 0.8rem;
  background-color: #e2e8f0;
  color: #4a5568;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.loading-badge i {
  font-size: 0.7rem;
  color: #4a5568;
}
.help-text {
  font-size: 0.8rem;
  color: #718096;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.help-text i {
  color: #4299e1;
}
.group-row {
  border-left: 4px solid;
  transition: all 0.2s ease;
}

.group-row:hover {
  box-shadow: 0 0 0 1px currentColor;
}

.leader-star-table {
  display: inline-block;
  background: #f6e05e;
  color: #744210;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 12px;
  border: 1px solid #ecc94b;
  margin-left: 5px;
  vertical-align: middle;
  white-space: nowrap;
  cursor: default;
}

.group-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: white;
  font-size: 10px;
  margin-left: 8px;
  vertical-align: middle;
}

.group-info-section {
  margin-bottom: 24px;
  padding: 16px;
  border-radius: 8px;
  background-color: #ffffff;
  border-left: 4px solid;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.group-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 20px;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
}

.group-badge i {
  font-size: 0.8rem;
}

.group-members-container {
  margin-top: 16px;
}

.loading-group-members,
.no-group-members {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #718096;
  font-size: 0.9rem;
  padding: 12px;
  background-color: #f7fafc;
  border-radius: 6px;
}

.group-member-count {
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #4a5568;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-member-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}

.group-member-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background-color: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  position: relative;
}

.group-member-item.is-leader {
  border-color: #d69e2e;
  background-color: #fffbeb;
}

.member-name {
  font-weight: 500;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.leader-star-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f6e05e;
  color: #744210;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  border: 1px solid #ecc94b;
}

.leader-star-sm {
  padding: 2px 6px;
  font-size: 0.75rem;
}

.member-cccd {
  font-size: 0.78rem;
  color: #92400e;
  background: #fef3c7;
  border-radius: 4px;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: monospace;
  letter-spacing: 0.05em;
  flex-wrap: wrap;
}

.member-birth-year {
  color: #92400e;
  font-family: inherit;
  font-weight: 600;
}

/* Leader badge on current booking's name field */
.name-with-leader {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.name-with-leader .edit-input {
  flex: 1;
  min-width: 0;
}

/* Gold star leader badge - replaces old green badge */
.leader-star-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f6e05e;
  color: #744210;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
  border: 1px solid #ecc94b;
}

/* Birth year hint below the input */
.birth-year-hint {
  margin-top: 5px;
  font-size: 0.8rem;
  color: #b7791f;
  background: #fefcbf;
  border: 1px solid #f6e05e;
  border-radius: 5px;
  padding: 4px 10px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* CCCD display box */
.cccd-display {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 6px;
  padding: 8px 12px;
  flex-wrap: wrap;
}

.cccd-display i {
  color: #2b6cb0;
  margin-top: 2px;
  flex-shrink: 0;
}

.cccd-number {
  font-family: monospace;
  font-size: 1rem;
  font-weight: 600;
  color: #2b6cb0;
  letter-spacing: 0.08em;
}

.cccd-address-small {
  font-size: 0.8rem;
  color: #4a5568;
  width: 100%;
  padding-left: 18px;
}

.member-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
}

.member-seat {
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 4px;
}

.member-status {
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75rem;
}

.view-member-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #e2e8f0;
  border: none;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s;
}

.view-member-btn:hover {
  background-color: #cbd5e0;
  color: #2d3748;
}

@media (max-width: 768px) {
  .group-member-list {
    grid-template-columns: 1fr;
  }
}

.booking-summary.has-group {
  border-left: 4px solid;
}
.highlight-row {
  background-color: rgba(var(--group-color-rgb), 0.05) !important;
}

.group-indicator {
  cursor: pointer;
  transition: transform 0.2s;
}

.group-indicator:hover {
  transform: scale(1.2);
}

.active-filters {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.filter-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: #f0f9ff;
  border-radius: 16px;
  border: 1px solid #bae6fd;
  font-size: 0.85rem;
  color: #0369a1;
}

.clear-filter {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #e0f2fe;
  border: none;
  color: #0369a1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.clear-filter:hover {
  background-color: #0369a1;
  color: white;
}

/* Add or update the seat grid styles */
.exchange-seat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 10px;
  margin: 15px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.seat-button {
  height: 40px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e9ecef;
    transform: translateY(-2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.seat-selected {
    background: #264039;
    color: white;
    border-color: #264039;
    transform: translateY(-2px);
    box-shadow: 0 2px 6px rgba(38, 64, 57, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.no-seats-message {
  grid-column: 1 / -1;
  padding: 20px;
  text-align: center;
  color: #721c24;
  background-color: #f8d7da;
  border-radius: 6px;
}

.selected-seat-info {
  margin-top: 10px;
  padding: 8px 12px;
  background: #e8f4f8;
  border-radius: 6px;
  color: #0c5460;
}

.selected-seat {
  font-weight: 600;
  color: #264039;
}

.price-comparison {
  margin: 15px 0;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #264039;
}

.price-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #dee2e6;
}

.price-difference {
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  padding-top: 10px;

  &.price-increase {
    color: #d35400;
  }

  &.price-refund {
    color: #27ae60;
  }
}

.loading-indicator {
  text-align: center;
  padding: 20px;
  color: #6c757d;

  i {
    margin-right: 8px;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Exchange Modal Styling */
.exchange-modal {
  max-width: 800px;
  max-height: 90vh;
}

.exchange-modal .modal-body {
  padding: 0;
  max-height: calc(90vh - 130px);
  overflow-y: auto;
}

.customer-info-summary {
  background: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
}

.customer-name {
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 5px;
}

.customer-contact {
  color: #6c757d;
  font-size: 0.9rem;
}

/* Multi-step process */
.exchange-process {
  padding: 20px;
}

.exchange-step {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border-left: 3px solid #4299e1;
}

.exchange-step:last-child {
  margin-bottom: 0;
}

.exchange-step.step-disabled {
  opacity: 0.7;
  background: #f8f9fa;
  pointer-events: none;
  border-left-color: #cbd5e0;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.step-number {
  background: #4299e1;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
}

.step-disabled .step-number {
  background: #cbd5e0;
}

.step-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

/* Form group styling */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #4a5568;
}

.form-group label i {
  margin-right: 5px;
  color: #4299e1;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.2s;
}

.form-control:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  outline: none;
}

/* Route Search Styling */
.route-search-wrapper {
  position: relative;
  margin-bottom: 10px;
}

.route-search-input {
  width: 100%;
  padding: 10px 40px 10px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 14px;
}

.route-search-input:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
  outline: none;
}

.route-search-input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.route-search-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  pointer-events: none;
}

.clear-search-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.clear-search-btn:hover {
  color: #718096;
}

.clear-search-btn i {
  font-size: 12px;
}

/* Price comparison */
.price-info {
  margin-top: 15px;
}

.price-comparison {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  border-left: 4px solid #4299e1;
}

.price-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #dee2e6;
}

.price-difference {
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  padding-top: 8px;
}

.price-increase {
  color: #e53e3e;
}

.price-refund {
  color: #38a169;
}

/* Seat selection */
.vehicle-preview {
  margin-bottom: 15px;
}

.vehicle-label {
  background: #4299e1;
  color: white;
  padding: 8px 12px;
  border-radius: 6px 6px 0 0;
  font-weight: 500;
}

.seat-grid {
  background: #f8f9fa;
  padding: 15px;
  border: 1px solid #e2e8f0;
  border-radius: 0 0 6px 6px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.seat-button {
  background: white;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 8px 5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.seat-button:hover {
  background: #edf2f7;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.seat-selected {
  background: #4299e1;
  color: white;
  border-color: #3182ce;
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(66, 153, 225, 0.3);
}

.selected-seat-info {
  background: #e6fffa;
  color: #234e52;
  padding: 10px 15px;
  border-radius: 6px;
  margin-top: 10px;
}

.selected-seat {
  font-weight: 600;
}

/* Loading and message states */
.loading-indicator,
.no-seats-message,
.instruction-message {
  padding: 20px;
  text-align: center;
  border-radius: 6px;
}

.loading-indicator {
  color: #4a5568;
}

.no-seats-message {
  background: #fff5f5;
  color: #c53030;
}

.instruction-message {
  background: #ebf8ff;
  color: #2c5282;
}

/* Button styling */
.exchange-confirm-btn {
  background: #4299e1;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.exchange-confirm-btn:hover:not(:disabled) {
  background: #3182ce;
}

.exchange-confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Vehicle type specific layouts */
.vehicle-type-16_slots .seat-grid {
  grid-template-columns: repeat(4, 1fr);
}

.vehicle-type-28_slots .seat-grid {
  grid-template-columns: repeat(4, 1fr);
}

.vehicle-type-45_slots .seat-grid {
  grid-template-columns: repeat(5, 1fr);
}

.vehicle-type-over_night .seat-grid {
  grid-template-columns: repeat(2, 1fr);
}

/* ===== EXPORT FEATURE ===== */
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: linear-gradient(135deg, #1d6f42 0%, #2e7d32 100%);
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 7px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(30,111,66,0.25);
  transition: all 0.2s ease;
}

.export-btn:hover {
  background: linear-gradient(135deg, #155230 0%, #256427 100%);
  box-shadow: 0 4px 12px rgba(30,111,66,0.35);
  transform: translateY(-1px);
}

.export-btn i {
  font-size: 0.95rem;
}

/* Export Modal */
.export-modal-content {
  max-width: 520px;
  width: 90%;
}

.export-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.export-date-row {
  display: flex;
  gap: 16px;
}

.export-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.export-field label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 5px;
}

.export-date-input {
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  font-size: 0.95rem;
  background: #f9fafc;
  transition: border-color 0.2s;
  outline: none;
}

.export-date-input:focus {
  border-color: #1d6f42;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(29,111,66,0.12);
}

.export-info {
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.84rem;
  color: #2c5282;
  line-height: 1.6;
  display: flex;
  gap: 8px;
}

.export-info i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #3182ce;
}

.export-loading {
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  border-radius: 8px;
  padding: 12px 16px;
  color: #276749;
  font-size: 0.88rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
}

.export-confirm-btn {
  background: linear-gradient(135deg, #1d6f42 0%, #2e7d32 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(29,111,66,0.25);
}

.export-confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #155230 0%, #256427 100%);
  box-shadow: 0 4px 12px rgba(29,111,66,0.35);
}

.export-confirm-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>


.invoice-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: #d1fae5;
  color: #065f46;
  text-decoration: none;
  transition: background 0.15s;
}
.invoice-link:hover { background: #a7f3d0; }
.no-invoice { color: #9ca3af; font-size: 11px; }
